import { createBrowserRouter, type RouteObject } from "react-router-dom";
import { RootLayout } from "@/layouts/RootLayout";
import { AppLayout } from "@/layouts/AppLayout";
import { AuthLayout } from "@/layouts/AuthLayout";
import { BrandLayout } from "@/layouts/BrandLayout";

import { LoginPage } from "@/pages/Login";
import { DashboardPage } from "@/pages/Dashboard";
import { HomeRedirect } from "@/pages/Home";
import { SettingsNotificationsPage } from "@/pages/SettingsNotifications";
import { LinkTelegramPage } from "@/pages/LinkTelegram";
import { UnsubscribePage } from "@/pages/Unsubscribe";

import { BrandOverviewPage } from "@/pages/brand/Overview";
import { BrandCalendarPage } from "@/pages/brand/Calendar";
import { QuickPostPage } from "@/pages/brand/QuickPost";
import { BrandCalendarUploadPage } from "@/pages/brand/CalendarUpload";
import { BrandAnalyticsPage } from "@/pages/brand/Analytics";
import { BrandAnalyticsInstagramPage } from "@/pages/brand/AnalyticsInstagram";
import { BrandAnalyticsYouTubePage } from "@/pages/brand/AnalyticsYouTube";
import { BrandAdsPage } from "@/pages/brand/Ads";
import { BrandCompetitorsPage } from "@/pages/brand/Competitors";
import { BrandComposePage } from "@/pages/brand/Compose";
import { BrandLibraryPage } from "@/pages/brand/Library";
import { BrandQuickPostProPage } from "@/pages/brand/QuickPostPro";
import { BrandReviewPage } from "@/pages/brand/Review";
import { BrandProjectDetailPage } from "@/pages/brand/ProjectDetail";
import { BrandPieceDetailPage } from "@/pages/brand/PieceDetail";
import { BrandSettingsAiUsagePage } from "@/pages/brand/SettingsAiUsage";
import { IdeasPage } from "@/pages/brand/Ideas";
import { BrandSettingsBrandPage } from "@/pages/brand/SettingsBrand";
import { BrandSettingsKitPage } from "@/pages/brand/SettingsKit";
import { BrandSettingsAiPage } from "@/pages/brand/SettingsAi";
import { BrandSettingsAiPromptsPage } from "@/pages/brand/SettingsAiPrompts";
import { BrandSettingsSchedulingPage } from "@/pages/brand/SettingsScheduling";
import { BrandSettingsAutomationPage } from "@/pages/brand/SettingsAutomation";
import { BrandSettingsTeamPage } from "@/pages/brand/SettingsTeam";
import { BrandSettingsAccountsPage } from "@/pages/brand/SettingsAccounts";
import { BrandSettingsInboxPage } from "@/pages/brand/SettingsInbox";
import { ZernioCallbackPage } from "@/pages/ZernioCallback";
import { AdminUsersPage } from "@/pages/AdminUsers";
import { AdminPromptsPage } from "@/pages/AdminPrompts";
import { PendingApprovalPage } from "@/pages/PendingApproval";
import { NotFoundPage } from "@/pages/NotFound";
import { InboxPage } from "@/pages/brand/Inbox";
import { BrandActivityPage } from "@/pages/brand/Activity";
import { InboxDmsPage } from "@/pages/brand/InboxDms";
import { InboxCommentsPage } from "@/pages/brand/InboxComments";
import { InboxReportsPage } from "@/pages/brand/InboxReports";
import { InboxAutomationsPage } from "@/pages/brand/InboxAutomations";

const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    children: [
      // Public routes (no auth required)
      {
        element: <AuthLayout />,
        children: [
          { path: "/login", element: <LoginPage /> },
          { path: "/pending-approval", element: <PendingApprovalPage /> },
          { path: "/u/link-telegram", element: <LinkTelegramPage /> },
          { path: "/u/zernio-callback", element: <ZernioCallbackPage /> },
          { path: "/u/unsubscribe", element: <UnsubscribePage /> },
        ],
      },

      // Catch-all 404 — handles its own auth gating + redirect
      { path: "*", element: <NotFoundPage /> },

      // Authenticated routes
      {
        element: <AppLayout />,
        children: [
          { index: true, element: <HomeRedirect /> },
          { path: "/dashboard", element: <DashboardPage /> },
          { path: "/settings/notifications", element: <SettingsNotificationsPage /> },
          { path: "/admin/users", element: <AdminUsersPage /> },
          { path: "/admin/prompts", element: <AdminPromptsPage /> },

          // Brand-scoped routes
          {
            path: "/:brandSlug",
            element: <BrandLayout />,
            children: [
              { index: true, element: <BrandOverviewPage /> },
              { path: "calendar", element: <BrandCalendarPage /> },
              { path: "quick-post", element: <QuickPostPage /> },
              { path: "compose", element: <BrandComposePage /> },
              { path: "library", element: <BrandLibraryPage /> },
              { path: "quick-post-pro", element: <BrandQuickPostProPage /> },
              { path: "calendar/upload", element: <BrandCalendarUploadPage /> },
              { path: "analytics", element: <BrandAnalyticsPage /> },
              { path: "analytics/instagram", element: <BrandAnalyticsInstagramPage /> },
              { path: "analytics/youtube", element: <BrandAnalyticsYouTubePage /> },
              { path: "ads", element: <BrandAdsPage /> },
              { path: "ads/:boostId", element: <BrandAdsPage /> },
              { path: "competitors", element: <BrandCompetitorsPage /> },
              { path: "review", element: <BrandReviewPage /> },
              { path: "activity", element: <BrandActivityPage /> },
              { path: "projects/:projectId", element: <BrandProjectDetailPage /> },
              { path: "pieces/:shortCode", element: <BrandPieceDetailPage /> },
              { path: "ideas", element: <IdeasPage /> },
              {
                path: "inbox",
                element: <InboxPage />,
                children: [
                  { path: "dms", element: <InboxDmsPage /> },
                  { path: "dms/:convId", element: <InboxDmsPage /> },
                  { path: "comments", element: <InboxCommentsPage /> },
                  { path: "comments/:commentId", element: <InboxCommentsPage /> },
                  { path: "reports", element: <InboxReportsPage /> },
                  { path: "automations", element: <InboxAutomationsPage /> },
                ],
              },
              { path: "settings/brand", element: <BrandSettingsBrandPage /> },
              { path: "settings/kit", element: <BrandSettingsKitPage /> },
              { path: "settings/ai", element: <BrandSettingsAiPage /> },
              { path: "settings/ai-prompts", element: <BrandSettingsAiPromptsPage /> },
              { path: "settings/scheduling", element: <BrandSettingsSchedulingPage /> },
              { path: "settings/automation", element: <BrandSettingsAutomationPage /> },
              { path: "settings/team", element: <BrandSettingsTeamPage /> },
              { path: "settings/accounts", element: <BrandSettingsAccountsPage /> },
              { path: "settings/ai-usage", element: <BrandSettingsAiUsagePage /> },
              { path: "settings/inbox", element: <BrandSettingsInboxPage /> },
            ],
          },
        ],
      },
    ],
  },
];

// Explicit type avoids TS2742 inference issue with @remix-run/router internals.
export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter(routes);
